<template>
    <v-container fluid class="pa-0">
        <v-row v-if="!this.create">
            <v-col>
                <v-card>
                    <v-card-title>
                        <v-row>
                            <v-col>
                                <h3 class="text-h5 pl-2">Runs</h3>
                            </v-col>
                        </v-row>
                    </v-card-title>
                </v-card>
                <v-data-table
                    :headers="headers"
                    :items="filteredRuns"
                    class="elevation-1"
                    clearable
                    :disabled="isLoading"
                    :loading="isLoading"
                    :items-per-page="25"
                    :search="search"
                    no-results-text="No matching records found (you may have to clear the protocol or status filter in the table header)"
                    :footer-props="{
                        itemsPerPageOptions: [25, 50, 100, -1],
                    }"
                >
                    <template v-slot:item.standards="{ item }">
                        <a :href="item.standards_url" target="_blank">
                            {{ item.standards }}
                        </a>
                    </template>

                    <!-- Filter 'Protocols' column -->
                    <template v-slot:header.protocol_name>
                        <div style="max-width: 200px">
                            <v-autocomplete
                                class="text-same-as-other-headers"
                                v-model="protocolId"
                                :items="analyticalProtocols"
                                item-title="name"
                                item-value="protocol_id"
                                clearable
                                placeholder="Protocols (All)"
                            >
                            </v-autocomplete>
                        </div>
                    </template>

                    <!-- 'Actions' column -->
                    <template v-slot:item.actions="{ item }">
                        <v-icon-btn color="primary" @click="download(item.standards, item.id)">
                            <v-icon>$download</v-icon>
                        </v-icon-btn>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <create-run v-if="this.create" @submit="onCreate"></create-run>

        <v-snackbar
            v-model="runCreateAlert.status"
            :timeout="3000"
            :location="true ? 'top' : undefined"
        >
            {{ runCreateAlert.message }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { defineComponent } from "vue";

import { mapGetters } from "vuex";
import { downloadStandardsExcel } from "@/services/api.services";
import axios from "../../lib/apiAxios";
import _ from "lodash";
import CreateRun from "../Runs/CreateRun";
import { downloadFile } from "@/lib/downloadFileUtil";

export default defineComponent({
    name: "RunsList",
    components: { CreateRun },

    props: {
        search: {
            type: String,
            default: "2",
        },
        tableHeaderColor: {
            type: String,
            default: "",
        },
    },

    async created() {
        this.create = this.$route.query.type === "create";
        // Fetch runs
        const runRes = await axios.get(`/runs/get_runs`);
        this.runData = runRes.data;
        // Fetch protocols
        const protocolRes = await axios.get(`/benchling/registries/protocols?isFunctional=true`);
        this.protocols = _.filter(protocolRes.data, { functional_team: "Analytical" });
    },

    computed: {
        ...mapGetters("requests", ["isLoading"]),
        analyticalProtocols() {
            return _.filter(this.protocols, { functional_team: "Analytical" });
        },
        filteredRuns() {
            return this.protocolId
                ? _.filter(this.runData, { protocol: this.protocolId })
                : this.runData;
        },
    },

    watch: {
        $route: function () {
            this.create = this.$route.query.type === "create";
        },
    },

    methods: {
        async download(requestCode, runId) {
            console.log("REQ", requestCode);
            console.log("Run Id", runId);
            const response = await downloadStandardsExcel(requestCode, runId);
            downloadFile(`${requestCode}-Standards.xlsx`, response);
        },
        async onCreate(data) {
            const protocol = data.protocols[0];
            console.log("Creating Run", data);
            try {
                const runRes = await axios.post(`/runs/createSamplesRequestAndRun`, {
                    protocolCode: protocol.protocol_id,
                    runDate: data.runDate,
                });
                if (runRes.status === 200) {
                    console.log(`Run ${runRes.data.id} successfully updated.`);
                    this.create = false;
                    this.runCreateAlert = {
                        status: true,
                        message: `Run ${runRes.data.id} successfully created. (It may take a minute for benchling link to resolve)`,
                    };
                    const refreshRunRes = await axios.get(`/runs/get_runs`);
                    this.runData = refreshRunRes.data;
                    this.$router.push({ path: "/runs" });
                }
            } catch (err) {
                console.error(err);
                this.updateAlert = {
                    status: true,
                    message: `There was an error during Run creation. ${err}`,
                };
            }
        },
    },

    data() {
        return {
            runCreateAlert: {
                status: false,
                message: "",
            },
            create: false,
            protocols: [],
            runData: [],
            data: null,
            headers: [
                {
                    title: "Run Id",
                    value: "run_label",
                    align: "center",
                    sortable: true,
                    width: "20",
                },
                {
                    title: "Run Date",
                    value: "run_date",
                    align: "center",
                    sortable: true,
                    width: "100",
                },
                {
                    title: "Standards",
                    value: "standards",
                    align: "left",
                    sortable: false,
                    width: "100",
                },
                {
                    title: "Processed Samps",
                    value: "processed_sample_count",
                    align: "left",
                    sortable: false,
                    width: "100",
                },
                {
                    title: "Protocol",
                    value: "protocol_name",
                    align: "left",
                    sortable: false,
                    width: "200",
                },
                {
                    title: "Actions",
                    value: "actions",
                    width: "50",
                },
            ],
            rowsPerPageItems: [20, 40, 60],
            pagination: {
                rowsPerPage: 20,
            },
            protocolId: null,
        };
    },
});
</script>

<style>
.text-same-as-other-headers {
    font-size: 0.75rem !important;
}
</style>
